import { useTranslation } from "react-i18next";
import { OtpCodeInput } from "../ui/OtpCodeInput";
import { OTP_CODE_LENGTH } from "../../domains/auth/hooks/useEmailOtp";
import type { OtpFailureReason } from "../../tools/analytics/domains/onboarding.analytics";

/**
 * The body of the code screen: the six boxes, the failure line, the resend.
 *
 * Deliberately headless. The title and the "we sent a code to X" line belong to
 * the HOST's own chrome — the onboarding carousel prints them in its header
 * band like every other slide, the login page in its own title block — and the
 * submit is the host's footer button. Printing them here gave the carousel a
 * second heading in a second style below its own, which is what made the slide
 * read as a foreign screen.
 *
 * The four failure states all render from the first version, before their
 * designs exist. A code screen that goes quiet on a wrong code is a login lost
 * without the user knowing why (PRD, decision 3 of 2026-09-04).
 */
export function EmailOtpForm({
  code,
  error,
  isBusy,
  cooldownRemaining,
  onCodeChange,
  onComplete,
  onResend,
  onChangeEmail,
  autoFocus,
}: {
  code: string;
  error: OtpFailureReason | null;
  isBusy: boolean;
  cooldownRemaining: number;
  onCodeChange: (code: string) => void;
  onComplete: (code: string) => void;
  onResend: () => void;
  /**
   * Back to the address field, for the mistyped address that will never receive
   * anything. `null` where the host already offers that way out — the signup
   * carousel's own Back button walks to the slide the address was typed on.
   * Without it on a host that does not, a typo is a dead end: no code arrives,
   * and resending sends it to the same wrong mailbox.
   */
  onChangeEmail: (() => void) | null;
  /**
   * Whether this screen is the one on stage.
   *
   * NOT a detail. The signup carousel keeps every slide mounted, and a focused
   * input inside an `overflow-hidden` track makes the browser scroll that track
   * to bring it into view. Focusing unconditionally therefore scrolled the whole
   * carousel three slides along, from the intro onwards, so every slide painted
   * the wrong content under a correct header and footer. Every other slide's
   * input is gated the same way (`autoFocus={isActive}`).
   */
  autoFocus: boolean;
}) {
  const { t } = useTranslation();

  const errorMessage =
    error === null
      ? null
      : error === "wrong_code"
        ? t("That code is not right. Check your email and try again.")
        : error === "expired"
          ? t("That code has expired. Ask for a new one.")
          : error === "too_many_attempts"
            ? t("Too many attempts. Ask for a new code.")
            : t(
                "Too many requests. Wait a moment before asking for a new code."
              );

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col gap-1">
        <OtpCodeInput
          value={code}
          length={OTP_CODE_LENGTH}
          onChange={onCodeChange}
          onComplete={onComplete}
          hasError={error !== null}
          autoFocus={autoFocus}
          disabled={isBusy}
          variant="mobile"
        />
        {errorMessage !== null && (
          <span className="pl-1 font-dm-sans text-ui-caption text-[#d32f2f]">
            {errorMessage}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2 items-start">
        <button
          type="button"
          className="bg-transparent border-none p-0 font-dm-sans text-ui-note font-semibold text-ds-mint-800 underline disabled:text-ds-slate-500 disabled:no-underline"
          disabled={cooldownRemaining > 0 || isBusy}
          onClick={onResend}
        >
          {cooldownRemaining > 0
            ? t("Resend the code ({{seconds}}s)", {
                seconds: cooldownRemaining,
              })
            : t("Resend the code")}
        </button>
        {onChangeEmail !== null && (
          <button
            type="button"
            className="bg-transparent border-none p-0 font-dm-sans text-ui-note font-semibold text-ds-mint-800 underline"
            onClick={onChangeEmail}
          >
            {t("Use a different email address")}
          </button>
        )}
      </div>
    </div>
  );
}
